import type { Board, Task } from "../api.ts";
import { C, mono, sans } from "../theme.ts";

// The food and training system, while it is being built.
//
// Read-only, and a filtered read at that: there is no store behind this screen.
// The tasks are rows in `tasks` and the prompts are files in the docs layer.
// If this ever gets its own table it becomes a second place to keep true, and
// it will go stale exactly the way board.md did.
//
// ───────────────────────────────────────────────────────────────────────────
// The three groups are a fixed list of ids, not a rule.
//
// A rule — by tier, by title, by whether the note holds a `prompt:` line —
// would look tidier and would silently capture the wrong task the first time
// someone adds one that happens to match. These fourteen were named in the
// brief of 31.08.2026. A task added to the area later appears nowhere on this
// screen until its id is written here on purpose, which is the intent.
//
// This whole screen is temporary. The menu entry that reaches it disappears
// when the last task in the area is done or parked, and this file goes with
// it. There is no cleanup task, because a cleanup task is a thing to remember.
// ───────────────────────────────────────────────────────────────────────────

const GROUPS: { name: string; ids: string[] }[] = [
  { name: "PREPARE", ids: ["T-156", "T-157", "T-158", "T-159", "T-133", "T-160"] },
  { name: "RUN",     ids: ["T-161", "T-162", "T-134", "T-163", "T-164"] },
  { name: "UNBLOCK", ids: ["T-165", "T-166", "T-167"] },
];

/** Blocked is computed rather than stored, so it is not in `state` to read. */
function stateOf(t: Task): { word: string; colour: string } {
  if (t.state === "done") return { word: "done", colour: C.good };
  if (t.state === "parked") return { word: "parked", colour: C.inkGhost };
  if (t.blocked) return { word: "waiting on something", colour: C.inkGhost };
  if (t.state === "waiting") return { word: "waiting", colour: C.inkFaint };
  return { word: "open", colour: C.inkFaint };
}

export function Build({ board, onOpen }: { board: Board; onOpen: (id: string) => void }) {
  const byId = new Map(board.tasks.map((t) => [t.id, t]));

  return (
    <div style={{ padding: "0 18px" }}>
      {GROUPS.map(({ name, ids }) => {
        const tasks = ids.map((id) => byId.get(id)).filter(Boolean) as Task[];
        if (!tasks.length) return null;
        return (
          <div key={name} style={{ marginBottom: 22 }}>
            <p style={{
              fontFamily: mono, fontSize: 11, color: C.inkFaint, letterSpacing: 1, margin: "0 0 8px",
            }}>{name}</p>

            {tasks.map((t) => {
              const state = stateOf(t);
              return (
                <div
                  key={t.id}
                  onClick={() => onOpen(t.id)}
                  style={{
                    background: C.card, borderRadius: 12, border: `1px solid ${C.rule}`,
                    borderLeft: `4px solid ${t.value_colour ?? C.ruleStrong}`,
                    padding: "12px 14px", marginBottom: 10, cursor: "pointer",
                    fontFamily: sans, opacity: t.state === "done" || t.state === "parked" ? 0.55 : 1,
                  }}
                >
                  <div style={{
                    fontSize: 14, lineHeight: 1.4,
                    textDecoration: t.state === "done" ? "line-through" : undefined,
                  }}>{t.title}</div>

                  {/* next_now rather than next: the read layer has already
                      applied the two presentation rules, and no view
                      re-implements them. */}
                  {t.next_now && (
                    <div style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.5, marginTop: 5 }}>
                      {t.next_now}
                    </div>
                  )}

                  <div style={{ fontFamily: mono, fontSize: 11, color: state.colour, marginTop: 6 }}>
                    {t.id} · {state.word}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
